import { useRef, useState, useCallback, type FC, useEffect, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'dibs-react-intl';
import { getRegionsList } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Chip, ChipPlaceholder } from 'dibs-elements/exports/Chip.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import ChevronRight from 'dibs-icons/exports/ChevronRight.ts';
import ChevronLeft from 'dibs-icons/exports/ChevronLeft.ts';
import Filter from 'dibs-icons/exports/Filter.ts';
import { updateUriRef } from '../../actions/filterActions.ts';
import { isFilterValueApplied } from '../helpers/appliedFilters/isFilterValueApplied.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { getValidSharedAppliedFilters } from '../SbSharedAppliedFilters/sbSharedAppliedFiltersHelpers.ts';
import { trackEvent, eventNameConstants, interactionTypeConstants } from 'dibs-tracking';

import { toggleFilterDisplay } from '../../actions/filterActions.ts';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';

import classnames from 'classnames';
import dibsCss from 'dibs-css';
import styles from './SbSharedQuickFilters.scss';

import { type SbSharedQuickFilters_itemSearch$key } from './__generated__/SbSharedQuickFilters_itemSearch.graphql.ts';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';

const itemSearchFragment = graphql`
    fragment SbSharedQuickFilters_itemSearch on ItemSearchQueryConnection {
        quickFilters {
            name
            values {
                displayName
                linkReference
                urlLabel
                currency
            }
        }
        appliedFilters {
            canBeDismissed
        }
        ...isFilterValueApplied_itemSearch
    }
`;

type Props = {
    itemSearch: SbSharedQuickFilters_itemSearch$key;
    scrollToTop?: () => void;
};

export const SbSharedQuickFilters: FC<Props> = ({ itemSearch: itemSearchRef, scrollToTop }) => {
    const { currency: userCurrency } = useCurrency();
    const dispatch = useDispatch();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { quickFilters } = itemSearch;
    const isClient = useClientState();
    const regionsList = isClient ? getRegionsList() : undefined;
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const isLoading = !isClient || refetchInFlight;
    const { isMobile } = useServerVarsContext();
    const scrollWhenRefetchDoneRef = useRef(false);

    // scroll to top when refetch is done
    const shouldScrollToTop = scrollToTop && !refetchInFlight && scrollWhenRefetchDoneRef.current;

    useEffect(() => {
        if (shouldScrollToTop) {
            scrollWhenRefetchDoneRef.current = false;
            scrollToTop();
        }
    }, [shouldScrollToTop, scrollToTop]);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const checkScrollability = useCallback(() => {
        if (isMobile) {
            return;
        }
        const el = scrollContainerRef.current;
        if (el) {
            const { scrollLeft, scrollWidth, clientWidth } = el;
            const newCanScrollLeft = scrollLeft > 1;
            if (newCanScrollLeft !== canScrollLeft) {
                setCanScrollLeft(newCanScrollLeft);
            }
            // Math.floor is needed below, sometimes scrollLeft comes back with .5 precision resulting in a 0.5 difference when we expect 0
            const newCanScrollRight = Math.floor(scrollWidth - scrollLeft - clientWidth) > 1;
            if (newCanScrollRight !== canScrollRight) {
                setCanScrollRight(newCanScrollRight);
            }
        }
    }, [canScrollLeft, canScrollRight, isMobile]);
    const handleScrollArrowClick = useCallback((direction: 'left' | 'right') => {
        const el = scrollContainerRef.current;
        if (el) {
            const scrollAmount = Math.max(el.clientWidth * 0.25, 200);
            const targetScrollLeft =
                direction === 'left' ? el.scrollLeft - scrollAmount : el.scrollLeft + scrollAmount;
            el.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
        }
    }, []);
    useEffect(() => {
        if (isMobile) {
            return () => {};
        }
        // initial scrollability check
        checkScrollability();
        // window resize can also affect scrollability
        window.addEventListener('resize', checkScrollability);
        return () => {
            window.removeEventListener('resize', checkScrollability);
        };
    }, [checkScrollability, isLoading, isMobile]);

    const appliedFilters = (itemSearch.appliedFilters || []).map(appliedFilter => ({
        canBeDismissed: !!appliedFilter?.canBeDismissed,
    }));
    const validAppliedFilters = getValidSharedAppliedFilters(appliedFilters);
    const appliedFiltersCount = validAppliedFilters.length;
    const getDisplayName = (filterName: string, displayName: string): ReactNode => {
        if (filterName === 'location') {
            return (
                <FormattedMessage
                    id="SbSharedQuickFilters.shipsFromLocation"
                    defaultMessage="Ships from {location}"
                    values={{ location: displayName }}
                />
            );
        }
        return displayName;
    };
    const getFiltersButton = useCallback(
        () => (
            <Button
                style="circle"
                size="small"
                type="transparent"
                className={classnames(styles.button, dibsCss.itemsCenter, dibsCss.gapXxsmall)}
                onClick={e => {
                    e.currentTarget.blur();
                    trackEvent(
                        {
                            category: 'results page facet selected',
                            action: 'refine link clicked',
                            eventName: eventNameConstants.EVENT_RESULTS_PAGE_FACET_SELECTED,
                            interaction_type:
                                interactionTypeConstants.INTERACTION_TYPE_FILTER_RESULTS_LINK_CLICKED,
                            step_interaction_name: 'show filters',
                        },
                        e
                    );
                    dispatch(toggleFilterDisplay());
                }}
                disabled={isLoading}
            >
                <Filter variation="20" />
                {isMobile ? (
                    <FormattedMessage
                        id="sb.SbMobileRefineMenuHeader.FiltersAndSort"
                        defaultMessage="Filters & Sort"
                    />
                ) : (
                    <FormattedMessage
                        id="SbSharedQuickFilters.showFilters"
                        defaultMessage="Show Filters"
                    />
                )}
                {appliedFiltersCount > 0 && <span>({appliedFiltersCount})</span>}
            </Button>
        ),
        [appliedFiltersCount, dispatch, isLoading, isMobile]
    );

    return (
        <>
            <div className={classnames(dibsCss.relative, dibsCss.overflowHidden, dibsCss.flexGrow)}>
                {!isMobile && (
                    <Link
                        className={classnames(
                            styles.arrow,
                            styles.arrowLeft,
                            dibsCss.absolute,
                            dibsCss.left0,
                            dibsCss.top0,
                            {
                                [styles.isHidden]: !canScrollLeft,
                            }
                        )}
                        onClick={() => handleScrollArrowClick('left')}
                    >
                        <ChevronLeft variation="20" />
                    </Link>
                )}
                <div
                    ref={scrollContainerRef}
                    onScroll={checkScrollability}
                    className={classnames(
                        styles.hideScrollbar,
                        dibsCss.overflowScroll,
                        dibsCss.flex,
                        dibsCss.flexGrow,
                        dibsCss.itemsCenter,
                        dibsCss.gapXxsmall,
                        {
                            [dibsCss.prSmall]: isMobile,
                        }
                    )}
                >
                    {isMobile && getFiltersButton()}
                    {isLoading
                        ? Array.from({ length: 3 }).map((_, index) => (
                              <ChipPlaceholder key={index} />
                          ))
                        : quickFilters?.map(quickFilter => {
                              const { values, name: filterName } = quickFilter || {};
                              if (!values || !filterName) {
                                  return null;
                              }

                              return values.map(filterValue => {
                                  const { urlLabel, linkReference, displayName, currency } =
                                      filterValue || {};
                                  if (!urlLabel || !linkReference || !displayName) {
                                      return null;
                                  }
                                  if (currency && currency !== userCurrency) {
                                      return null; // skip pricing filter values with wrong currency
                                  }
                                  if (
                                      filterName === 'location' &&
                                      !regionsList?.includes(urlLabel)
                                  ) {
                                      return null; // skip location for users outside of that region
                                  }

                                  const isActive = isFilterValueApplied({
                                      itemSearch,
                                      filterName,
                                      urlLabel,
                                  });
                                  if (isActive) {
                                      return null; // skip active filters too
                                  }

                                  return (
                                      <Chip
                                          key={`${filterName}-${urlLabel}`}
                                          onClick={() => {
                                              scrollWhenRefetchDoneRef.current = true;
                                              dispatch(
                                                  updateUriRef({
                                                      filterName,
                                                      filterValue,
                                                      ga: { interaction_type: 'quick filter' },
                                                      isDeselected: isActive,
                                                  })
                                              );
                                          }}
                                      >
                                          {getDisplayName(filterName, displayName)}
                                      </Chip>
                                  );
                              });
                          })}
                </div>
                {!isMobile && (
                    <Link
                        className={classnames(
                            styles.arrow,
                            styles.arrowRight,
                            dibsCss.absolute,
                            dibsCss.right0,
                            dibsCss.top0,
                            {
                                [styles.isHidden]: !canScrollRight,
                            }
                        )}
                        onClick={() => handleScrollArrowClick('right')}
                    >
                        <ChevronRight variation="20" />
                    </Link>
                )}
            </div>
            {!isMobile && getFiltersButton()}
        </>
    );
};
